// One project, every record it holds: documents, the categories, the journal, the
// knowledge base, the brain's question history, the crew, phases, the
// processing queue, and dictations. Each tab renders the same shapes the
// console renders — the contract reuse is what makes that a fact rather than
// an aspiration.
// The writes — upload, open a day, add a note, ask — are the admin exercising
// the same services a tenant user does (architecture/0027).
import { useRef, useState } from "react";
import { BookOpen, FolderOpen, MessageSquareText, Mic } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import {
  asOutline,
  CREW_ROLE_LABELS,
  CrewRoleSchema,
  DocTypeSchema,
  FAMILY_ROLE_LABELS,
  FILE_STATUS_LABELS,
  MADE_BY_LABELS,
  ORGANISE_GAP_LABELS,
  removeRow,
  docTypeLabel,
  type CrewRole,
  type DocType,
  type PhaseInput,
} from "@gb/contracts";
import { DataTable } from "@/components/gb/data-table";
import { EmptyState } from "@/components/gb/empty-state";
import { ErrorDialog } from "@/components/gb/error-dialog";
import { PageHeader } from "@/components/gb/page-header";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/gb/field";
import { BackfillPanel } from "./backfill-panel";
import { CategoriesTab } from "./categories-tab";
import { useProject } from "./projects-api";
import { usePhases, useJobs } from "./projects-api";
import { useDocuments, useUploadDocument } from "../documents/documents-api";
import { useJournalDays, useDictations, useCreateJournalEntry } from "../journal/journal-api";
import { useThreads, useCreateThread, useSetThreadVisibility } from "../chat/chat-api";
import { useBrainItems, useBrainSummary, useCreateBrainNote, useDeleteBrainNote } from "../brain/brain-api";
import { useSetPhases } from "./projects-api";
import {
  useAddCrewMember,
  useConfirmCertificate,
  useDeleteCertificate,
  useOffboard,
  usePersonCertificates,
  useReonboard,
  useRoster,
  useUploadCertificate,
} from "../crew/crew-api";

export function ProjectDetailPage() {
  const { tenantId = "", projectId = "" } = useParams();
  const project = useProject(tenantId, projectId);

  if (project.isError) return <EmptyState icon={FolderOpen} title="Project not found" />;
  if (!project.data) return null;
  const p = project.data;

  return (
    <div>
      <div className="mb-1 text-[0.75rem]">
        <Link to={`/tenants/${tenantId}`} className="text-brand hover:underline">
          ← Tenant
        </Link>
      </div>
      <PageHeader eyebrow="Project" title={p.name} />
      <div className="mt-1 font-mono text-[0.75rem] text-faint">
        {[p.region, p.country, p.spec, p.status].filter(Boolean).join(" · ")}
      </div>

      <Tabs defaultValue="documents" className="mt-6">
        <TabsList>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="journal">Journal</TabsTrigger>
          <TabsTrigger value="knowledge">Knowledge</TabsTrigger>
          <TabsTrigger value="brain">Questions</TabsTrigger>
          <TabsTrigger value="crew">Crew</TabsTrigger>
          <TabsTrigger value="phases">Phases</TabsTrigger>
          <TabsTrigger value="jobs">Jobs</TabsTrigger>
          <TabsTrigger value="dictations">Dictations</TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="mt-4">
          <DocumentsTab tenantId={tenantId} projectId={projectId} />
        </TabsContent>
        <TabsContent value="categories" className="mt-4">
          <CategoriesTab tenantId={tenantId} projectId={projectId} />
        </TabsContent>
        <TabsContent value="journal" className="mt-4">
          <JournalTab tenantId={tenantId} projectId={projectId} />
        </TabsContent>
        <TabsContent value="knowledge" className="mt-4">
          <KnowledgeTab tenantId={tenantId} projectId={projectId} />
        </TabsContent>
        <TabsContent value="brain" className="mt-4">
          <BrainTab tenantId={tenantId} projectId={projectId} />
        </TabsContent>
        <TabsContent value="crew" className="mt-4">
          <CrewTab tenantId={tenantId} projectId={projectId} />
        </TabsContent>
        <TabsContent value="phases" className="mt-4">
          <PhasesTab tenantId={tenantId} projectId={projectId} />
        </TabsContent>
        <TabsContent value="jobs" className="mt-4">
          <JobsTab tenantId={tenantId} projectId={projectId} />
        </TabsContent>
        <TabsContent value="dictations" className="mt-4">
          <DictationsTab tenantId={tenantId} projectId={projectId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

type TabProps = { tenantId: string; projectId: string };

function DocumentsTab({ tenantId, projectId }: TabProps) {
  const documents = useDocuments(tenantId, projectId);
  // The Brain page's "Not organised yet" count, the same four gaps (RET-12).
  const summary = useBrainSummary(tenantId, projectId);
  const unorganised = summary.data?.unorganisedDocuments ?? 0;
  const upload = useUploadDocument(tenantId, projectId);
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-[0.8rem] text-muted">
          {unorganised > 0
            ? `Not organised yet: ${unorganised} ${unorganised === 1 ? "file" : "files"}. The project check, the versions or the links have not done ${unorganised === 1 ? "it" : "them"}.`
            : null}
        </div>
        {/* The 3-call chain lives in the mutation; parsing shows up as
            `processingStatus` on the invalidated listing, same as the console. */}
        <input
          ref={fileInput}
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
            e.target.value = "";
          }}
        />
        <Button size="sm" disabled={upload.isPending} onClick={() => fileInput.current?.click()}>
          {upload.isPending ? "Uploading…" : "Upload document"}
        </Button>
      </div>
      <ErrorDialog
        title="That upload didn’t finish"
        error={upload.isError ? String(upload.error.message) : null}
      />
      <DataTable
      columns={[
        {
          key: "file",
          header: `Documents${documents.data ? ` (${documents.data.total})` : ""}`,
          render: (d) => (
            <div className="min-w-0">
              <div className="truncate text-[0.85rem] font-semibold text-ink">{d.filename}</div>
              <div className="truncate font-mono text-[0.72rem] text-faint">
                {d.mimeType}
                {d.pageCount ? ` · ${d.pageCount}p` : ""}
              </div>
            </div>
          ),
        },
        {
          key: "type",
          header: "Type",
          width: 150,
          render: (d) => (
            <div className="truncate text-[0.75rem] text-muted" title={d.docType ?? undefined}>
              {d.docType ?? "—"}
            </div>
          ),
        },
        {
          // The tenant's category the file lives in, and who put it there; the
          // others its card lists come after, in full on hover
          // (`document-ai/0032`). Names are the tenant's, from the payload.
          key: "category",
          header: "Category",
          width: 200,
          render: (d) => {
            const also = d.alsoRelevant.map((c) => c.name).join(", ");
            // A file whose main key left the tenant's list is not sorted, and
            // can still list others, so the second line stands without it.
            const second = [
              d.category ? MADE_BY_LABELS[d.category.madeBy] : null,
              also ? `also ${also}` : null,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <div className="min-w-0">
                <div className="truncate text-[0.75rem] text-muted" title={d.category?.name}>
                  {d.category?.name ?? "Not sorted yet"}
                </div>
                {second && (
                  <div
                    className="truncate text-[0.68rem] text-faint"
                    title={also ? `Also relevant: ${also}` : undefined}
                  >
                    {second}
                  </div>
                )}
              </div>
            );
          },
        },
        {
          // Where the file sits among its versions (`document-ai/0016`). A
          // file with no relatives is the current version, on its own.
          key: "role",
          header: "Role",
          width: 150,
          render: (d) => (
            <div className="min-w-0" title={d.family.name ?? undefined}>
              <div className="truncate text-[0.75rem] text-muted">
                {FAMILY_ROLE_LABELS[d.family.role]}
              </div>
              {d.question && (
                <div className="truncate text-[0.68rem] font-semibold text-warn">
                  Question waiting
                </div>
              )}
            </div>
          ),
        },
        {
          // What the file is, apart from where it sits among its versions
          // (`document-ai/0013`, `0016` §2). A null status is no claim —
          // nobody has looked, or the file names no project — so it shows the
          // same dash the other columns use rather than an invented word.
          //
          // The backoffice lists every file, hidden ones included
          // (`data-model/0013` §8). That is why this column exists: a hidden
          // file has to be told apart here, not dropped.
          key: "fileStatus",
          header: "Status",
          width: 150,
          render: (d) => (
            <div className="min-w-0" title={d.status.reason ?? undefined}>
              <div className="truncate text-[0.75rem] text-muted">
                {d.status.status ? FILE_STATUS_LABELS[d.status.status] : "—"}
              </div>
              {d.status.hidden && (
                <div className="truncate text-[0.68rem] font-semibold text-warn">
                  Hidden from answers
                </div>
              )}
              {/* The check would have hidden it and asked instead
                  (2026-09-26): still in answers until a person decides. */}
              {d.status.question && (
                <div className="truncate text-[0.68rem] font-semibold text-warn">
                  Asked: {FILE_STATUS_LABELS[d.status.question.status]}?
                </div>
              )}
            </div>
          ),
        },
        {
          // What of the file nothing read, and what no step organised
          // (2026-09-26, RET-12): the same states the Brain page shows.
          key: "processing",
          header: "Processing",
          width: 170,
          render: (d) => (
            <div className="min-w-0">
              <span className="font-mono text-[0.72rem] text-muted">{d.processingStatus}</span>
              {d.readFailure && (
                <div className="truncate text-[0.68rem] font-semibold text-warn" title={d.readFailure}>
                  Not read in full
                </div>
              )}
              {d.picturesNotRead !== null && d.picturesNotRead > 0 && (
                <div className="truncate text-[0.68rem] font-semibold text-warn">
                  {d.picturesNotRead} {d.picturesNotRead === 1 ? "picture" : "pictures"} not read
                </div>
              )}
              {d.organiseGaps.length > 0 && (
                <div
                  className="truncate text-[0.68rem] text-muted"
                  title={d.organiseGaps.map((gap) => ORGANISE_GAP_LABELS[gap]).join(", ")}
                >
                  Not organised yet
                </div>
              )}
            </div>
          ),
        },
      ]}
      rows={documents.data?.documents ?? []}
      getRowKey={(d) => d.id}
      onSelect={(d) => void navigate(`/tenants/${tenantId}/projects/${projectId}/documents/${d.id}`)}
      emptyMessage="No documents."
      />
    </>
  );
}

function JournalTab({ tenantId, projectId }: TabProps) {
  const days = useJournalDays(tenantId, projectId);
  const create = useCreateJournalEntry(tenantId, projectId);
  const navigate = useNavigate();
  const [date, setDate] = useState("");
  return (
    <>
      <form
        className="mb-3 flex items-end justify-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!date || create.isPending) return;
          create.mutate(
            { date },
            {
              onSuccess: () =>
                void navigate(`/tenants/${tenantId}/projects/${projectId}/journal/${date}`),
            },
          );
        }}
      >
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-[180px]"
          aria-label="Day to open"
        />
        <Button type="submit" size="sm" disabled={!date || create.isPending}>
          Open a day
        </Button>
      </form>
      <ErrorDialog
        title="That day didn’t open"
        error={create.isError ? String(create.error.message) : null}
      />
      <DataTable
      columns={[
        {
          key: "date",
          header: "Day",
          width: 130,
          render: (d) => <span className="font-mono text-[0.8rem] text-ink">{d.date}</span>,
        },
        {
          key: "contents",
          header: "Contents",
          render: (d) => (
            <span className="text-[0.78rem] text-muted">
              {d.noteCount} note{d.noteCount === 1 ? "" : "s"} · {d.photoCount} photo
              {d.photoCount === 1 ? "" : "s"}
              {d.hasDailyEntry ? " · daily log" : ""}
            </span>
          ),
        },
        {
          key: "signed",
          header: "Signed off",
          width: 100,
          render: (d) => (
            <span className="font-mono text-[0.75rem] text-muted">
              {d.signedOff ? "yes" : "—"}
            </span>
          ),
        },
      ]}
      rows={days.data?.days ?? []}
      getRowKey={(d) => d.date}
      onSelect={(d) => void navigate(`/tenants/${tenantId}/projects/${projectId}/journal/${d.date}`)}
      emptyMessage="No journal days."
      />
    </>
  );
}

function KnowledgeTab({ tenantId, projectId }: TabProps) {
  const items = useBrainItems(tenantId, projectId);
  const createNote = useCreateBrainNote(tenantId, projectId);
  const deleteNote = useDeleteBrainNote(tenantId, projectId);
  const navigate = useNavigate();
  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const submit = (): void => {
    if (!title.trim() || !body.trim() || createNote.isPending) return;
    createNote.mutate(
      { title: title.trim(), body: body.trim() },
      {
        onSuccess: () => {
          setComposing(false);
          setTitle("");
          setBody("");
        },
      },
    );
  };

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button size="sm" onClick={() => setComposing(true)}>
          Add note
        </Button>
      </div>
      <ErrorDialog
        title="That didn’t save"
        error={
          createNote.isError
            ? String(createNote.error.message)
            : deleteNote.isError
              ? String(deleteNote.error.message)
              : null
        }
      />
      {items.data && items.data.items.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="The Brain is empty"
          description="Upload documents or add a note."
        />
      ) : (
        <DataTable
          columns={[
            {
              key: "title",
              header: `Items${items.data ? ` (${items.data.total})` : ""}`,
              render: (item) => (
                <div className="min-w-0">
                  <div className="truncate text-[0.85rem] font-semibold text-ink">
                    {item.kind === "document" ? item.filename : item.title}
                  </div>
                  <div className="truncate text-[0.72rem] text-faint">
                    {item.kind === "document" ? item.docType : item.snippet}
                  </div>
                </div>
              ),
            },
            {
              key: "source",
              header: "Source",
              width: 110,
              render: (item) => (
                <span className="font-mono text-[0.72rem] text-muted">
                  {item.kind === "document" ? "document" : item.sourceType}
                </span>
              ),
            },
            {
              key: "actions",
              header: "",
              width: 90,
              render: (item) =>
                // Only hand-written notes are deletable — a project-doc row
                // would re-create itself from the ingest path (the 409 rule).
                item.kind === "entry" && item.sourceType === "upload" ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={deleteNote.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNote.mutate(item.id);
                    }}
                  >
                    Delete
                  </Button>
                ) : null,
            },
          ]}
          rows={items.data?.items ?? []}
          getRowKey={(item) => item.id}
          onSelect={(item) => {
            if (item.kind === "document") {
              void navigate(`/tenants/${tenantId}/projects/${projectId}/documents/${item.id}`);
            }
          }}
          emptyMessage="The Brain is empty."
        />
      )}

      <Dialog open={composing} onOpenChange={setComposing}>
        <DialogContent className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Add a note to the Brain</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <Field label="Title">
              {(props) => (
                <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} />
              )}
            </Field>
            <Field label="Note">
              {(props) => (
                <Textarea
                  {...props}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={6}
                />
              )}
            </Field>
            <Button
              type="submit"
              size="sm"
              disabled={!title.trim() || !body.trim() || createNote.isPending}
            >
              {createNote.isPending ? "Saving…" : "Save note"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BrainTab({ tenantId, projectId }: TabProps) {
  const threads = useThreads(tenantId, projectId);
  const createThread = useCreateThread(tenantId, projectId);
  const visibility = useSetThreadVisibility(tenantId, projectId);
  const navigate = useNavigate();
  // The flip being asked about, held until the admin confirms. Both
  // directions confirm: promoting puts the question in the tenant's history,
  // unsharing pulls out something they may have already seen.
  const [pending, setPending] = useState<{
    threadId: string;
    title: string;
    next: boolean;
  } | null>(null);

  const newQuestion = (): void => {
    if (createThread.isPending) return;
    createThread.mutate(undefined, {
      onSuccess: (thread) =>
        void navigate(`/tenants/${tenantId}/projects/${projectId}/chat/${thread.id}`),
    });
  };

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button size="sm" disabled={createThread.isPending} onClick={newQuestion}>
          {createThread.isPending ? "Opening…" : "New question"}
        </Button>
      </div>
      <ErrorDialog
        title="That didn’t work"
        error={
          createThread.isError
            ? String(createThread.error.message)
            : visibility.isError
              ? String(visibility.error.message)
              : null
        }
      />
      {threads.data && threads.data.threads.length === 0 ? (
        <EmptyState icon={MessageSquareText} title="Nothing asked yet" />
      ) : (
        <DataTable
          columns={[
            {
              key: "title",
              header: `Questions${threads.data ? ` (${threads.data.total})` : ""}`,
              render: (t) => <div className="truncate text-[0.85rem] text-ink">{t.title}</div>,
            },
            {
              key: "turns",
              header: "Turns",
              width: 70,
              render: (t) => (
                <span className="font-mono text-[0.75rem] text-muted">{t.messageCount}</span>
              ),
            },
            {
              key: "visibility",
              header: "Tenant sees it",
              width: 150,
              render: (t) => (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={visibility.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPending({ threadId: t.id, title: t.title, next: !t.sharedWithTenant });
                  }}
                >
                  {t.sharedWithTenant ? "Yes — unshare" : "Promote"}
                </Button>
              ),
            },
            {
              key: "at",
              header: "Asked",
              width: 120,
              render: (t) => (
                <span className="font-mono text-[0.75rem] text-faint">
                  {new Date(t.createdAt).toLocaleDateString()}
                </span>
              ),
            },
          ]}
          rows={threads.data?.threads ?? []}
          getRowKey={(t) => t.id}
          onSelect={(t) => void navigate(`/tenants/${tenantId}/projects/${projectId}/chat/${t.id}`)}
          emptyMessage="Nothing asked yet."
        />
      )}

      <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>
              {pending?.next ? "Promote to tenant-visible?" : "Unshare from the tenant?"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[0.85rem] leading-[1.5] text-muted">
            {pending?.next
              ? "The question and its answer will appear in the tenant's own question history, as if one of their users had asked it."
              : "The question will disappear from the tenant's history. If someone there has already read it, it will vanish under them."}
          </p>
          <p className="truncate text-[0.82rem] font-semibold text-ink">“{pending?.title}”</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={visibility.isPending}
              onClick={() => {
                if (!pending) return;
                visibility.mutate(
                  { threadId: pending.threadId, sharedWithTenant: pending.next },
                  { onSuccess: () => setPending(null) },
                );
              }}
            >
              {visibility.isPending ? "Saving…" : pending?.next ? "Promote" : "Unshare"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CrewTab({ tenantId, projectId }: TabProps) {
  const roster = useRoster(tenantId, projectId);
  const addMember = useAddCrewMember(tenantId, projectId);
  const offboard = useOffboard(tenantId, projectId);
  const reonboard = useReonboard(tenantId, projectId);
  const [personId, setPersonId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState<CrewRole>("installer");

  if (roster.isError) {
    // Most commonly: the project has no country yet, so no compliance regime.
    return <EmptyState icon={FolderOpen} title="No roster" description={String(roster.error)} />;
  }
  const people = roster.data
    ? [...roster.data.onSite, ...roster.data.onboarding, ...roster.data.offSite]
    : [];
  const selected = people.find((m) => m.id === personId) ?? null;

  return (
    <>
      <form
        className="mb-3 flex items-center justify-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim() || addMember.isPending) return;
          addMember.mutate(
            { name: name.trim(), role },
            { onSuccess: () => setName("") },
          );
        }}
      >
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          aria-label="New crew member name"
          className="w-[220px]"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as CrewRole)}
          aria-label="Role"
          className="min-h-[46px] rounded-input border-[1.5px] border-border bg-white px-3 text-[0.9rem] text-ink"
        >
          {CrewRoleSchema.options.map((value) => (
            <option key={value} value={value}>
              {CREW_ROLE_LABELS[value]}
            </option>
          ))}
        </select>
        <Button type="submit" size="sm" disabled={!name.trim() || addMember.isPending}>
          {addMember.isPending ? "Adding…" : "Add member"}
        </Button>
      </form>
      <ErrorDialog
        title="That didn’t work"
        error={
          addMember.isError
            ? String(addMember.error.message)
            : offboard.isError
              ? String(offboard.error.message)
              : reonboard.isError
                ? String(reonboard.error.message)
                : null
        }
      />
      <DataTable
        columns={[
          {
            key: "name",
            header: "Person",
            render: (m) => (
              <div className="min-w-0">
                <div className="truncate text-[0.85rem] font-semibold text-ink">{m.name}</div>
                <div className="truncate text-[0.72rem] text-faint">{m.roleLabel}</div>
              </div>
            ),
          },
          {
            key: "state",
            header: "State",
            width: 130,
            render: (m) => (
              <span className="font-mono text-[0.72rem] text-muted">
                {m.offSite ? "off site" : m.onboarding ? "onboarding" : m.cleared ? "cleared" : m.overall}
              </span>
            ),
          },
          {
            key: "actions",
            header: "",
            width: 110,
            render: (m) => (
              <Button
                variant="ghost"
                size="sm"
                disabled={offboard.isPending || reonboard.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  if (m.offSite) reonboard.mutate(m.id);
                  else offboard.mutate(m.id);
                }}
              >
                {m.offSite ? "Reonboard" : "Offboard"}
              </Button>
            ),
          },
        ]}
        rows={people}
        getRowKey={(m) => m.id}
        onSelect={(m) => setPersonId(m.id)}
        emptyMessage="No crew."
      />
      <Dialog open={personId !== null} onOpenChange={(open) => !open && setPersonId(null)}>
        <DialogContent className="max-w-[620px]">
          <DialogHeader>
            <DialogTitle>{selected ? `${selected.name} — certificates` : "Certificates"}</DialogTitle>
          </DialogHeader>
          {personId && (
            <CertificateManager
              tenantId={tenantId}
              projectId={projectId}
              personId={personId}
              docLabels={roster.data?.jurisdiction?.docLabels ?? {}}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * A person's certificates, writable: upload (presign → read → suggestion),
 * confirm the fields — pre-filled from the model's suggestion when there is
 * one, since a suggestion never counts until a person accepts it — and
 * delete. Labels resolve jurisdiction-first, shared defaults second.
 */
function CertificateManager({
  tenantId,
  projectId,
  personId,
  docLabels,
}: {
  tenantId: string;
  projectId: string;
  personId: string;
  /** Jurisdiction-localised names, off the roster; `docTypeLabel` fills gaps. */
  docLabels: Record<string, string>;
}) {
  const certificates = usePersonCertificates(tenantId, personId);
  const upload = useUploadCertificate(tenantId);
  const confirm = useConfirmCertificate(tenantId, projectId);
  const removeCert = useDeleteCertificate(tenantId, projectId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState<DocType>(DocTypeSchema.options[0]);
  const [confirming, setConfirming] = useState<{
    docId: string;
    issuingBody: string;
    reference: string;
    issuedDate: string;
    expiryDate: string;
  } | null>(null);

  const openConfirm = (
    docId: string,
    seed: { issuingBody?: string | null; reference?: string | null; issuedDate?: string | null; expiryDate?: string | null },
  ): void =>
    setConfirming({
      docId,
      issuingBody: seed.issuingBody ?? "",
      reference: seed.reference ?? "",
      issuedDate: seed.issuedDate ?? "",
      expiryDate: seed.expiryDate ?? "",
    });

  return (
    <div className="max-h-[540px] space-y-4 overflow-y-auto">
      <div className="flex items-end gap-2">
        <Field label="Add a certificate" className="flex-1">
          {(props) => (
            <select
              {...props}
              value={docType}
              onChange={(e) => setDocType(e.target.value as DocType)}
              className="min-h-[46px] w-full rounded-input border-[1.5px] border-border bg-white px-3 text-[0.9rem] text-ink"
            >
              {DocTypeSchema.options.map((value) => (
                <option key={value} value={value}>
                  {docTypeLabel(value, docLabels)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <input
          ref={fileInput}
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              upload.mutate(
                { personId, type: docType, file },
                {
                  onSuccess: ({ certificate, suggestion }) =>
                    openConfirm(certificate.id, suggestion),
                },
              );
            }
            e.target.value = "";
          }}
        />
        <Button
          variant="secondary"
          disabled={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {upload.isPending ? "Reading…" : "Upload"}
        </Button>
      </div>
      <ErrorDialog
        title="That didn’t save"
        error={
          upload.isError
            ? String(upload.error.message)
            : confirm.isError
              ? String(confirm.error.message)
              : removeCert.isError
                ? String(removeCert.error.message)
                : null
        }
      />

      {confirming && (
        <form
          className="space-y-3 rounded-card border-[1.5px] border-brand-border-strong bg-brand-tint p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!confirming.issuingBody.trim() || !confirming.expiryDate || confirm.isPending) return;
            confirm.mutate(
              {
                personId,
                docId: confirming.docId,
                input: {
                  issuingBody: confirming.issuingBody.trim(),
                  reference: confirming.reference.trim() || undefined,
                  issuedDate: confirming.issuedDate || undefined,
                  expiryDate: confirming.expiryDate,
                },
              },
              { onSuccess: () => setConfirming(null) },
            );
          }}
        >
          <div className="text-[0.82rem] font-semibold text-ink">
            Confirm the fields — nothing counts towards compliance until accepted.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Issuing body">
              {(props) => (
                <Input
                  {...props}
                  value={confirming.issuingBody}
                  onChange={(e) => setConfirming({ ...confirming, issuingBody: e.target.value })}
                />
              )}
            </Field>
            <Field label="Reference" optional>
              {(props) => (
                <Input
                  {...props}
                  value={confirming.reference}
                  onChange={(e) => setConfirming({ ...confirming, reference: e.target.value })}
                />
              )}
            </Field>
            <Field label="Issued" optional>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={confirming.issuedDate}
                  onChange={(e) => setConfirming({ ...confirming, issuedDate: e.target.value })}
                />
              )}
            </Field>
            <Field label="Expires">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={confirming.expiryDate}
                  onChange={(e) => setConfirming({ ...confirming, expiryDate: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={!confirming.issuingBody.trim() || !confirming.expiryDate || confirm.isPending}
            >
              {confirm.isPending ? "Confirming…" : "Confirm certificate"}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {(certificates.data ?? []).map((c) => (
          <div
            key={c.id}
            className="flex items-center gap-3 rounded-card border-[1.5px] border-border bg-surface px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-[0.88rem] font-semibold text-ink">
                  {docTypeLabel(c.type, docLabels)}
                </span>
                <span
                  className={
                    c.confirmed
                      ? "rounded-full bg-ok-bg px-2 py-0.5 font-display text-[0.6rem] font-semibold tracking-[0.06em] text-ok uppercase"
                      : "rounded-full bg-warn-bg px-2 py-0.5 font-display text-[0.6rem] font-semibold tracking-[0.06em] text-warn uppercase"
                  }
                >
                  {c.confirmed ? "Confirmed" : "Unconfirmed"}
                </span>
              </div>
              {(c.issuingBody || c.reference) && (
                <div className="mt-0.5 truncate text-[0.78rem] text-muted">
                  {[c.issuingBody, c.reference].filter(Boolean).join(" · ")}
                </div>
              )}
              {(c.issuedDate || c.expiryDate) && (
                <div className="mt-0.5 font-mono text-[0.72rem] text-faint">
                  {c.issuedDate ?? "issued —"} → {c.expiryDate ?? "no expiry"}
                </div>
              )}
              {c.filename && (
                <div className="mt-0.5 truncate font-mono text-[0.7rem] text-faint">{c.filename}</div>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {!c.confirmed && (
                <Button variant="ghost" size="sm" onClick={() => openConfirm(c.id, c)}>
                  Confirm
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-danger"
                disabled={removeCert.isPending}
                onClick={() => {
                  if (!window.confirm("Remove this certificate?")) return;
                  removeCert.mutate({ personId, docId: c.id });
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
        {certificates.data?.length === 0 && (
          <p className="text-[0.82rem] text-muted">
            Nothing on file yet — upload the first certificate above.
          </p>
        )}
      </div>
    </div>
  );
}

function PhasesTab({ tenantId, projectId }: TabProps) {
  const phases = usePhases(tenantId, projectId);
  const setPhases = useSetPhases(tenantId, projectId);
  // Null until the admin opens the editor; then a working copy seeded from
  // the server rows. Editing a copy keeps Cancel honest.
  const [draft, setDraft] = useState<PhaseInput[] | null>(null);

  const startEditing = (): void =>
    setDraft(
      (phases.data ?? []).map((ph) => ({
        id: ph.id,
        // Carried through, or a save would put every sub-phase back on one
        // level (2026-10-03).
        level: ph.level,
        name: ph.name,
        startDate: ph.startDate,
        endDate: ph.endDate,
        note: ph.note,
      })),
    );

  const setRow = (index: number, patch: Partial<PhaseInput>): void =>
    setDraft((rows) => rows!.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  if (draft === null) {
    return (
      <>
        <div className="mb-3 flex justify-end">
          <Button size="sm" onClick={startEditing}>
            Edit phases
          </Button>
        </div>
        <DataTable
          columns={[
            {
              key: "name",
              header: "Phase",
              render: (ph) => (
                <span className="text-[0.85rem] text-ink" style={{ paddingLeft: ph.level * 20 }}>
                  {ph.name}
                </span>
              ),
            },
            {
              key: "dates",
              header: "Dates",
              width: 210,
              render: (ph) => (
                <span className="font-mono text-[0.75rem] text-muted">
                  {ph.startDate ?? "?"} → {ph.endDate ?? "?"}
                </span>
              ),
            },
            {
              key: "origin",
              header: "Origin",
              width: 90,
              render: (ph) => <span className="font-mono text-[0.72rem] text-faint">{ph.origin}</span>,
            },
          ]}
          rows={phases.data ?? []}
          getRowKey={(ph) => ph.id}
          emptyMessage="No phases."
        />
      </>
    );
  }

  const canSave = draft.every((row) => row.name.trim()) && !setPhases.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) return;
        setPhases.mutate(
          // Already an outline: Remove lifts a row's sub-phases (`removeRow`).
          // `asOutline` stays as the guard on a list the save would refuse.
          asOutline(draft).map((row) => ({
            ...row,
            name: row.name.trim(),
            startDate: row.startDate || null,
            endDate: row.endDate || null,
          })),
          { onSuccess: () => setDraft(null) },
        );
      }}
    >
      <ErrorDialog
        title="The phases didn’t save"
        error={setPhases.isError ? String(setPhases.error.message) : null}
      />
      <div className="space-y-2">
        {draft.map((row, index) => (
          <div
            key={row.id ?? `new-${index}`}
            className="flex items-center gap-2 rounded-lg border border-border-inner bg-white p-2"
            style={{ marginLeft: row.level * 20 }}
          >
            <Input
              value={row.name}
              onChange={(e) => setRow(index, { name: e.target.value })}
              placeholder="Phase name"
              aria-label={`Phase ${index + 1} name`}
              className="flex-1"
            />
            <Input
              type="date"
              value={row.startDate ?? ""}
              onChange={(e) => setRow(index, { startDate: e.target.value || null })}
              aria-label={`Phase ${index + 1} start`}
              className="w-[160px]"
            />
            <Input
              type="date"
              value={row.endDate ?? ""}
              onChange={(e) => setRow(index, { endDate: e.target.value || null })}
              aria-label={`Phase ${index + 1} end`}
              className="w-[160px]"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-danger"
              // Its sub-phases are lifted into its place, as on the dashboard
              // (`removeRow`, `@gb/contracts`). A plain filter moved them
              // under the phase above (branch review, 2026-10-03).
              onClick={() => setDraft((rows) => removeRow(rows!, index))}
            >
              Remove
            </Button>
          </div>
        ))}
        {draft.length === 0 && (
          <p className="text-[0.8rem] text-muted">No phases yet — add the first one.</p>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setDraft((rows) => [...rows!, { name: "", level: 0 }])}
        >
          Add phase
        </Button>
        <Button type="submit" size="sm" disabled={!canSave}>
          {setPhases.isPending ? "Saving…" : "Save phases"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(null)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function JobsTab({ tenantId, projectId }: TabProps) {
  const jobs = useJobs(tenantId, projectId);
  return (
    <>
      {/* The steps whose code is newer than what it wrote (2026-09-26, RET-09). */}
      <BackfillPanel tenantId={tenantId} projectId={projectId} />
      <DataTable
      columns={[
        {
          key: "kind",
          header: "Job",
          width: 140,
          render: (j) => <span className="font-mono text-[0.78rem] text-ink">{j.kind}</span>,
        },
        {
          key: "status",
          header: "Status",
          width: 110,
          render: (j) => <span className="font-mono text-[0.75rem] text-muted">{j.status}</span>,
        },
        // Which bundle and which file. The service always sent both and the
        // table showed neither, so a stuck job could not be tied to the import
        // it was holding up (found mapping the code for Phase 3f).
        {
          key: "import",
          header: "Import",
          width: 100,
          render: (j) => (
            <span className="font-mono text-[0.72rem] text-muted" title={j.importId ?? undefined}>
              {j.importId ? j.importId.slice(0, 8) : "—"}
            </span>
          ),
        },
        {
          key: "document",
          header: "File",
          width: 100,
          render: (j) =>
            j.documentId ? (
              <Link
                to={`/tenants/${tenantId}/projects/${projectId}/documents/${j.documentId}`}
                className="font-mono text-[0.72rem] text-brand hover:underline"
                title={j.documentId}
              >
                {j.documentId.slice(0, 8)}
              </Link>
            ) : (
              <span className="font-mono text-[0.72rem] text-faint">—</span>
            ),
        },
        {
          key: "error",
          header: "Error",
          render: (j) => (
            <span className="truncate text-[0.75rem] text-danger">{j.error ?? ""}</span>
          ),
        },
        {
          key: "attempts",
          header: "Attempts",
          width: 80,
          render: (j) => <span className="font-mono text-[0.75rem] text-muted">{j.attempts}</span>,
        },
        {
          key: "at",
          header: "Started",
          width: 150,
          render: (j) => (
            <span className="font-mono text-[0.72rem] text-faint">
              {new Date(j.createdAt).toLocaleString()}
            </span>
          ),
        },
      ]}
      rows={jobs.data ?? []}
      getRowKey={(j) => j.id}
      emptyMessage="No processing jobs."
    />
    </>
  );
}

function DictationsTab({ tenantId, projectId }: TabProps) {
  const dictations = useDictations(tenantId, projectId);
  if (dictations.data && dictations.data.dictations.length === 0) {
    return <EmptyState icon={Mic} title="No dictations" />;
  }
  return (
    <div className="space-y-3">
      {(dictations.data?.dictations ?? []).map((d) => (
        <div key={d.id} className="rounded-lg border border-border bg-white p-4">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-mono text-[0.78rem] text-ink">{d.date}</span>
            <span className="font-mono text-[0.7rem] text-faint">
              transcription: {d.transcriptionStatus} · extraction: {d.extractionStatus}
            </span>
          </div>
          <p className="mt-2 text-[0.82rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
            {d.transcript ?? "(no transcript)"}
          </p>
          {d.suggestion && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[0.72rem] text-muted">
                Extraction suggestion
              </summary>
              <pre className="mt-1 overflow-x-auto rounded bg-canvas p-2 font-mono text-[0.7rem] text-ink-prose">
                {JSON.stringify(d.suggestion, null, 2)}
              </pre>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}
